import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";

async function getCroppedBlob(imageSrc, cropPixels) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = imageSrc;
  });

  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    size,
    size
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
  });
}

export function PhotoCropModal({ imageSrc, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  async function confirm() {
    if (!pixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, pixels);
      await onConfirm(blob);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ft-crop-backdrop" data-testid="photo-crop-modal">
      <div className="ft-crop-modal" role="dialog" aria-modal="true" aria-label="Adjust photo">
        <div className="ft-crop-head">
          <div>
            <span className="eyebrow">Frame the photo</span>
            <h2>Adjust portrait</h2>
          </div>
          <button className="ft-crop-close" onClick={onCancel} aria-label="Close" data-testid="crop-cancel">×</button>
        </div>

        <div className="ft-crop-area">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={1}
            maxZoom={4}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="ft-crop-controls">
          <span aria-hidden="true">−</span>
          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            aria-label="Zoom"
            data-testid="crop-zoom-slider"
          />
          <span aria-hidden="true">+</span>
        </div>

        <p className="ft-crop-hint">Drag to reposition, use the slider to zoom.</p>

        <div className="ft-crop-actions">
          <button className="secondary" onClick={onCancel} data-testid="crop-cancel-btn">Cancel</button>
          <button onClick={confirm} disabled={saving || !pixels} data-testid="crop-save-btn">
            {saving ? "Saving…" : "Use photo"}
          </button>
        </div>
      </div>
    </div>
  );
}
